/**
 * オートパイロット（docs/03_simulation.md 9 章）。プレイヤーと同じ操作量（ブレーク・スピードバー）を出す。
 * 使うのは見えている情報だけ：自分のバリオ、見えている雲、旋回中の他の選手、自分の流され方から分かる風、地形。
 */
import { FLIGHT } from '../../config/balance';
import { wrapPi } from '../../core/math';
import { dirToAzimuth, type Vec2 } from '../../core/vec';
import { Biome } from '../../core/world';
import { sunPosition } from '../weather/sun';
import type { ControlInput } from '../flight/physics';
import type { FlightState } from '../types';
import type { WeatherModel } from '../weather/model';
import type { TerrainSampler } from '../world/terrain';

export interface AutopilotContext {
  weather: WeatherModel;
  terrain: TerrainSampler;
  timeOfDayS: number;
  cloudBaseM: number;
  /** 旋回して上昇している他の選手の位置 */
  climbers: Vec2[];
  sinkFactor: number;
  style: 'attack' | 'defend';
  /** 日没後など、とにかく降りる */
  mustLand: boolean;
}

/** 旋回中のおおよその沈下（m/s） */
const CIRCLING_SINK = 1.3;

/** デバッグ用：最後に狙った地点（画面なしの検証ツールで読む） */
export const autopilotDebug = { aim: null as Vec2 | null, low: false, thermalId: -1 };

const LANDABLE = new Set<number>([Biome.Meadow, Biome.Road, Biome.Wetland]);

function isLandable(terrain: TerrainSampler, x: number, z: number): boolean {
  return LANDABLE.has(terrain.biomeAt(x, z)) && terrain.slopeDegAt(x, z) < 15;
}

function chooseLanding(f: FlightState, ctx: AutopilotContext): Vec2 {
  const { terrain } = ctx;
  const p = f.pos;
  const agl = p.y - terrain.heightAt(p.x, p.z);
  if (
    Math.hypot(f.target.x - p.x, f.target.z - p.z) < Math.max(1500, agl * 6) &&
    isLandable(terrain, f.target.x, f.target.z)
  ) {
    return { ...f.target };
  }
  let best: Vec2 | null = null;
  let bestScore = -Infinity;
  const reach = Math.max(300, agl * 6);
  for (let a = -120; a <= 120; a += 20) {
    const h = f.headingRad + (a * Math.PI) / 180;
    for (const frac of [0.25, 0.5, 0.75, 1]) {
      const d = reach * frac;
      const x = p.x + Math.sin(h) * d;
      const z = p.z - Math.cos(h) * d;
      if (!isLandable(terrain, x, z)) continue;
      const score = -Math.hypot(f.target.x - x, f.target.z - z) - Math.abs(a) * 5;
      if (score > bestScore) {
        bestScore = score;
        best = { x, z };
      }
    }
  }
  return best ?? { x: p.x + Math.sin(f.headingRad) * 300, z: p.z - Math.cos(f.headingRad) * 300 };
}

/** 向き heading に滑空したときの、先の地形との余裕（最小値） */
function clearance(f: FlightState, ctx: AutopilotContext, heading: number): number {
  const p = f.pos;
  const sinkPerM = 1.25 / Math.max(f.airspeedMs, 6);
  let min = Infinity;
  for (const d of [150, 300, 600, 1000, 1500]) {
    const x = p.x + Math.sin(heading) * d;
    const z = p.z - Math.cos(heading) * d;
    // 近いほど余裕を厳しく見る
    const margin = d <= 300 ? 40 : 60;
    min = Math.min(min, p.y - d * sinkPerM - ctx.terrain.heightAt(x, z) - margin);
  }
  return min;
}

const OFFSETS = [0, 20, -20, 40, -40, 60, -60, 90, -90, 120, -120, 150, -150, 180].map(
  (d) => (d * Math.PI) / 180,
);

/** 狙いたい向きに近い、地形に当たらない向き（どれも駄目なら、いちばん余裕のある向き） */
function safeHeading(f: FlightState, ctx: AutopilotContext, desired: number): number {
  let best = desired;
  let bestC = -Infinity;
  for (const o of OFFSETS) {
    const h = desired + o;
    const c = clearance(f, ctx, h);
    if (c >= 0) return h;
    if (c > bestC) {
      bestC = c;
      best = h;
    }
  }
  return best;
}

/** 遠くまで地形に当たらず滑空できる向き（目標の向きに近いほど良い）。低いときの逃げ道 */
function escapeHeading(f: FlightState, ctx: AutopilotContext, desired: number, sinkPerM: number): number {
  const p = f.pos;
  let best = desired;
  let bestScore = -Infinity;
  for (let a = -180; a < 180; a += 20) {
    const h = desired + (a * Math.PI) / 180;
    let min = Infinity;
    for (const d of [300, 800, 1500, 2500, 4000]) {
      const g = ctx.terrain.heightAt(p.x + Math.sin(h) * d, p.z - Math.cos(h) * d);
      min = Math.min(min, p.y - d * sinkPerM - g);
    }
    // 余裕が 250m あれば十分。それ以上は目標の向きに近いほうを選ぶ
    const score = Math.min(min, 250) - Math.abs(a) * 0.8;
    if (score > bestScore) {
      bestScore = score;
      best = h;
    }
  }
  return best;
}

function steer(f: FlightState, desired: number, out: ControlInput): void {
  const err = wrapPi(desired - f.headingRad);
  const asym = Math.min(Math.max(err * 1.1, -0.6), 0.6);
  out.brakeR = asym > 0 ? asym : 0;
  out.brakeL = asym < 0 ? -asym : 0;
}

function circle(f: FlightState, out: ControlInput): void {
  const a = 0.55;
  out.brakeR = f.ap.circleDir > 0 ? a : 0;
  out.brakeL = f.ap.circleDir < 0 ? a : 0;
  out.bar = 0;
}

export function autopilot(f: FlightState, ctx: AutopilotContext, dt: number, out: ControlInput): void {
  const { terrain, weather, timeOfDayS: t, cloudBaseM: cb } = ctx;
  const ap = f.ap;
  const p = f.pos;
  const ground = terrain.heightAt(p.x, p.z);
  const agl = p.y - ground;
  out.brakeL = 0;
  out.brakeR = 0;
  out.bar = 0;
  out.bigEars = 0;
  ap.tried = ap.tried.filter((s) => s.untilS > t);

  // 着陸
  const nearTarget = ap.mode === 'final' && Math.hypot(f.target.x - p.x, f.target.z - p.z) < 400;
  const airborneS = ctx.timeOfDayS - f.launchTimeS;
  const tooLong = airborneS > FLIGHT.maxFlightS;
  // 本当に低くなったら着陸に入る（斜面沿いを低く飛ぶのは普通なので、しきい値は低め。離陸直後は除く）
  // 低くても、谷へ滑空して逃げられるなら降りない。逃げ場がない（または地面すれすれ）ときだけ着陸
  const cornered =
    agl < 60 && f.varioMs < 0.3 && (agl < 25 || clearance(f, ctx, safeHeading(f, ctx, f.headingRad)) < 0);
  const lowAndSinking = cornered && airborneS > 60;
  if (ap.mode !== 'landing' && (lowAndSinking || ctx.mustLand || nearTarget || tooLong)) {
    ap.mode = 'landing';
    ap.landing = chooseLanding(f, ctx);
  }
  if (ap.mode === 'landing') {
    const L = ap.landing ?? { ...f.target };
    const d = Math.hypot(L.x - p.x, L.z - p.z);
    if (d > 180) steer(f, dirToAzimuth(L.x - p.x, L.z - p.z), out);
    else circle(f, out);
    // 着陸地点の近くで高さが余っていれば（日没後はいつでも）、ビッグイヤーで降りる。
    // 斜面の上昇風などで降りられずにいるときも、ビッグイヤーで沈下を増やす（浮いたままにならないように）
    const stuck = f.varioMs > -0.8 && agl > 40;
    if ((d < 800 || ctx.mustLand) && (agl > 250 || stuck)) out.bigEars = 1;
    if (agl < 25) {
      out.brakeL = Math.max(out.brakeL, 0.15);
      out.brakeR = Math.max(out.brakeR, 0.15);
    }
    return;
  }

  // 離陸直後は、離陸の向きにまっすぐ飛んで斜面から離れる
  if (airborneS < 45) {
    steer(f, f.launchHeadingRad, out);
    return;
  }

  const wind = weather.windAt(p.x, p.y, p.z, t, { x: 0, z: 0 });
  const toTx = f.target.x - p.x;
  const toTz = f.target.z - p.z;
  const distT = Math.hypot(toTx, toTz);
  const headingT = dirToAzimuth(toTx, toTz);

  // 雷雲を避ける
  if (weather.stormDistance(p.x, p.z) < 3000) {
    let away = headingT;
    let far = -Infinity;
    for (const s of weather.state.storms) {
      if (s.phase !== 'mature' && s.phase !== 'dissipating') continue;
      const d = Math.hypot(p.x - s.x, p.z - s.z) - s.radiusM;
      if (-d > far) {
        far = -d;
        away = dirToAzimuth(p.x - s.x, p.z - s.z);
      }
    }
    ap.avoid = { heading: away, untilS: t + 120 };
  }

  if (ap.avoid && ap.avoid.untilS > t) {
    steer(f, ap.avoid.heading, out);
    return;
  }
  ap.avoid = null;

  // 目標まで滑空で届くか
  const trimSpeed = 37 / 3.6;
  const trimSink = 1.1 / ctx.sinkFactor;
  const along = distT > 1 ? (wind.x * toTx + wind.z * toTz) / distT : 0;
  const groundSpeed = Math.max(trimSpeed + along, 3);
  const required = terrain.heightAt(f.target.x, f.target.z) + 120 + (distT * trimSink) / groundSpeed;
  // 途中の地形（尾根）も越えられるか
  let pathClear = p.y >= required;
  for (let k = 1; pathClear && k <= 8; k++) {
    const d = (distT * k) / 9;
    const x = p.x + (toTx / distT) * d;
    const z = p.z + (toTz / distT) * d;
    if (p.y - (d * trimSink) / groundSpeed < terrain.heightAt(x, z) + 80) pathClear = false;
  }
  if (pathClear) ap.mode = 'final';
  else if (ap.mode === 'final') ap.mode = 'cruise';

  const lift = f.liftMs;
  if (ap.mode === 'final') {
    steer(f, safeHeading(f, ctx, headingT), out);
    out.bar = along < -3 ? 0.5 : 0.2;
    return;
  }

  if (ap.mode === 'search' || ap.mode === 'climb') {
    ap.timerS += dt;
    const tau = ap.mode === 'search' ? 8 : 20;
    ap.liftAvg += (lift - ap.liftAvg) * (1 - Math.exp(-dt / tau));
    const top = ctx.style === 'attack' ? 0.8 : 0.95;
    // 抜ける基準は「実際に上がれている速さ」（空気の上昇から旋回中の沈下 約 1.3m/s を引いたもの）
    const weak = CIRCLING_SINK + (ctx.style === 'attack' ? 1.0 : 0.3);
    if (ap.mode === 'search') {
      // 旋回中の沈下を上回る上昇があれば、上昇に移る
      // 低いときは、ゼロシンクに近い弱い上昇でも粘る（センタリングで強くなることが多い）
      const need = agl < 1200 ? 0 : 0.2;
      if (ap.timerS > 15 && ap.liftAvg > CIRCLING_SINK + need) {
        ap.mode = 'climb';
        ap.timerS = 0;
      } else if (ap.timerS > 45) {
        ap.tried.push({ x: p.x, z: p.z, untilS: t + 600 });
        ap.mode = 'cruise';
      }
    } else if (p.y >= cb * top - 50 || (ap.timerS > 60 && ap.liftAvg < weak)) {
      ap.tried.push({ x: p.x, z: p.z, untilS: t + 300 });
      ap.mode = 'cruise';
    }
    if (ap.mode === 'search' || ap.mode === 'climb') {
      circle(f, out);
      return;
    }
  }

  // 巡航：上昇に当たったら（または狙ったサーマルの位置に着いたら）旋回を試す
  const nearTried = ap.tried.some((s) => Math.hypot(s.x - p.x, s.z - p.z) < 400);
  const atAim = ap.aim !== null && Math.hypot(ap.aim.x - p.x, ap.aim.z - p.z) < 120;
  // 地面すれすれで旋回すると斜面に落ちるので、低いときは強い上昇でなければ回らない
  const canCircle = agl > 150 || lift > CIRCLING_SINK + 0.5;
  if ((lift > 0.4 || atAim) && canCircle && p.y < cb * 0.93 && !nearTried && weather.thermalRatio(t) > 0) {
    ap.aim = null;
    ap.mode = 'search';
    ap.timerS = 0;
    ap.liftAvg = lift;
    ap.circleDir = f.bankRad >= 0 ? 1 : -1;
    circle(f, out);
    return;
  }

  // 雲（その下のサーマル）や旋回中の選手のうち、届いて得になるものへ向かう。
  // 低いときは視野を広げ、近くて確実に届くものを優先する（高さを保つ）。
  let aim: Vec2 = f.target;
  if (p.y < cb * 0.92 && weather.thermalRatio(t) > 0) {
    const low = p.y < cb - 900 || agl < 800;
    const cone = ((low ? 110 : ctx.style === 'attack' ? 35 : 50) * Math.PI) / 180;
    const budget = Math.max(3000, distT * 0.25);
    let best = Infinity;
    let bestId = -1;
    const consider = (x: number, z: number, baseM: number, id = -1): void => {
      const d = Math.hypot(x - p.x, z - p.z);
      if (d < 150 || d > 15_000) return;
      if (Math.abs(wrapPi(dirToAzimuth(x - p.x, z - p.z) - headingT)) > cone) return;
      if (ap.tried.some((s) => Math.hypot(s.x - x, s.z - z) < 500)) return;
      const alongAim = (wind.x * (x - p.x) + wind.z * (z - p.z)) / d;
      const sinkPerM = trimSink / Math.max(trimSpeed + alongAim, 3);
      const arrival = p.y - d * sinkPerM;
      if (arrival < baseM + 150 || cb - arrival < 300) return;
      // 途中の地形（尾根）を越えられるか
      for (let k = 1; k <= 4; k++) {
        const dk = (d * k) / 5;
        const gx = p.x + ((x - p.x) * k) / 5;
        const gz = p.z + ((z - p.z) * k) / 5;
        if (p.y - dk * sinkPerM < terrain.heightAt(gx, gz) + 30) return;
      }
      const detour = d + Math.hypot(f.target.x - x, f.target.z - z) - distT;
      if (!low && detour > budget) return;
      const cost = low ? d + detour * 0.3 : detour;
      if (cost < best) {
        best = cost;
        aim = { x, z };
        bestId = id;
      }
    };
    for (const c of weather.state.clouds) {
      if (c.towering > 0.3) continue;
      const th = weather.thermalById(c.thermalId);
      if (!th) continue;
      // 着く頃に崩れていそうな雲（サーマルの寿命の終わり）は狙わない
      const eta = Math.hypot(c.x - p.x, c.z - p.z) / trimSpeed;
      if (t - th.bornS + eta + 300 > th.lifeS) continue;
      // 着いたときの高さでのサーマルの中心（サーマルは風で傾いているので、到着の高さを繰り返し見積もる）
      let center = weather.thermalCenterAt(th, p.y, wind);
      for (let k = 0; k < 3; k++) {
        const d = Math.hypot(center.x - p.x, center.z - p.z);
        const along = d > 1 ? (wind.x * (center.x - p.x) + wind.z * (center.z - p.z)) / d : 0;
        const arrivalAlt = Math.max(p.y - (d * trimSink) / Math.max(trimSpeed + along, 3), th.groundM);
        center = weather.thermalCenterAt(th, arrivalAlt, wind);
      }
      consider(center.x, center.z, th.groundM, th.id);
    }
    for (const c of ctx.climbers) consider(c.x, c.z, terrain.heightAt(c.x, c.z));
    autopilotDebug.thermalId = bestId;

    // 届く雲がなければ、日の当たる斜面（サーマルが出やすい場所）へ向かう
    if (aim === f.target && p.y < cb - 300 && weather.thermalRatio(t) > 0.2) {
      const sun = sunPosition(t).dir;
      let bestScore = 0;
      for (let a = -100; a <= 100; a += 20) {
        const h = headingT + (a * Math.PI) / 180;
        for (const d of [800, 1600, 2800, 4200]) {
          const x = p.x + Math.sin(h) * d;
          const z = p.z - Math.cos(h) * d;
          const g = terrain.heightAt(x, z);
          const arrival = p.y - (d * trimSink) / trimSpeed;
          if (arrival < g + 120 || ap.tried.some((s) => Math.hypot(s.x - x, s.z - z) < 600)) continue;
          const grad = terrain.gradientAt(x, z);
          const inv = 1 / Math.hypot(grad.gx, 1, grad.gz);
          const incidence = (-grad.gx * sun.x + sun.y - grad.gz * sun.z) * inv;
          const score = incidence * (1 - Math.abs(a) / 250) - (arrival - g) / 8000;
          if (score > bestScore) {
            bestScore = score;
            aim = { x, z };
          }
        }
      }
    }
  }
  ap.aim = aim === f.target ? null : aim;
  autopilotDebug.aim = ap.aim;
  autopilotDebug.low = p.y < cb - 900;
  // 低くて目標へも上昇へも届かないなら、斜面に沿って沈むより、開けた谷の上へ滑空して逃げる
  let heading = dirToAzimuth(aim.x - p.x, aim.z - p.z);
  if (ap.aim === null && agl < 450) heading = escapeHeading(f, ctx, headingT, trimSink / trimSpeed);
  steer(f, safeHeading(f, ctx, heading), out);
  let bar = ctx.style === 'attack' ? 0.25 : 0;
  if (along < -3) bar += 0.35;
  if (lift < -1) bar += 0.3;
  out.bar = Math.min(bar, 1);
}
